<script lang="ts">
import { type PropType, defineComponent } from 'vue'

/**
 * A wrapper component that handles the state of an asynchronous function.
 * It provides slots for pending, rejected, and resolved states.
 *
 * To lazy-load a component, register it with defineAsyncComponent
 * and use AsyncWrapper to handle its loading state. The use of the
 * ElementVisibility is not necessary in this case.
 *
 * Example usage:
 * components: {
 *  ComponentA: defineAsyncComponent(() => import('./ComponentA.vue')),
 * }
 * ....
 * <AsyncWrapper :await="loadComponent">
 *   <template #pending>
 *     <div>Loading...</div>
 *   </template>
 *   <template #rejected>
 *     <div>Error loading component.</div>
 *   </template>
 *   <template #default>
 *     <ComponentA />
 *   </template>
 * </AsyncWrapper>
 */
export default defineComponent({
  props: {
    /**
     * The asynchronous function to execute. This function should return a promise.
     * The result of the promise will be passed to the default slot when resolved.
     *
     * @type {() => Promise<any>}
     * @required
     */
    await: {
      type: Function as PropType<() => Promise<any>>,
      required: true,
    },
  },
  data() {
    return {
      pending: false,
      rejected: false,
      value: undefined as any,
      error: undefined as any,
    }
  },
  watch: {
    await: {
      async handler() {
        // Guard against async function not being available on mount
        if (typeof this.await !== 'function') {
          return
        }

        try {
          this.pending = true
          this.rejected = false
          this.error = undefined

          this.value = await this.await()

          if (!this.value) {
            throw new Error(
              'AsyncWrapper: No value returned from async function'
            )
          }
        } catch (error: any) {
          this.rejected = true
          this.error = error
        } finally {
          this.pending = false
        }
      },
      immediate: true,
    },
  },
})
</script>

<template>
  <slot v-if="pending" name="pending">
    <div class="text-center text-slate-600">
      <p class="text-lg">Loading...</p>
    </div>
  </slot>

  <slot v-else-if="rejected" :error="error" name="rejected">
    <div class="text-center text-slate-600">
      <p class="text-lg">Unable to load data right now.</p>
      <p class="mt-2">Please check back later.</p>
    </div>
  </slot>

  <slot v-else-if="value"> Resolved={{ value }} </slot>
</template>
